import type * as GAME from 'vibegame';

export interface Theme {
  // Backgrounds
  background: number;
  backgroundSecondary: number;
  backgroundRaised: number;

  // Text
  foreground: number;
  textSecondary: number;
  textTertiary: number;
  textKnockout: number;

  // Grey scale
  grey000: number;
  grey100: number;
  grey200: number;
  grey300: number;
  grey400: number;
  grey500: number;
  grey600: number;
  grey700: number;
  grey800: number;
  grey900: number;
  grey1000: number;
  grey1100: number;

  // Teal scale
  teal100: number;
  teal200: number;
  teal300: number;
  teal400: number;
  teal500: number;
  teal600: number;
  teal700: number;
  teal800: number;
  teal900: number;
  teal1000: number;

  // Blue scale
  blue100: number;
  blue200: number;
  blue300: number;
  blue400: number;
  blue500: number;
  blue600: number;
  blue700: number;
  blue800: number;
  blue900: number;
  blue1000: number;

  // Purple scale
  purple100: number;
  purple200: number;
  purple300: number;
  purple400: number;
  purple500: number;
  purple600: number;
  purple700: number;
  purple800: number;
  purple900: number;
  purple1000: number;

  // Pink scale
  pink100: number;
  pink200: number;
  pink300: number;
  pink400: number;
  pink500: number;
  pink600: number;
  pink700: number;
  pink800: number;
  pink900: number;
  pink1000: number;

  // Red scale
  red100: number;
  red200: number;
  red300: number;
  red400: number;
  red500: number;
  red600: number;
  red700: number;
  red800: number;
  red900: number;
  red1000: number;

  // Yellow scale
  yellow100: number;
  yellow200: number;
  yellow300: number;
  yellow400: number;
  yellow500: number;
  yellow600: number;
  yellow700: number;
  yellow800: number;
  yellow900: number;
  yellow1000: number;

  // Green scale
  green100: number;
  green200: number;
  green300: number;
  green400: number;
  green500: number;
  green600: number;
  green700: number;
  green800: number;
  green900: number;
  green1000: number;

  // Semantic defaults
  teal: number;
  blue: number;
  purple: number;
  pink: number;
  red: number;
  yellow: number;
  green: number;
  muted: number;
  positive: number;
  negative: number;
}

export const LIGHT: Theme = {
  // Backgrounds
  background: 0xffffff,
  backgroundSecondary: 0xfafafa,
  backgroundRaised: 0xffffff,

  // Text
  foreground: 0x28282a,
  textSecondary: 0x747474,
  textTertiary: 0xa8a8a8,
  textKnockout: 0xffffff,

  // Grey scale
  grey000: 0xffffff,
  grey100: 0xf2f2f2,
  grey200: 0xebebeb,
  grey300: 0xe6e6e6,
  grey400: 0xebebeb,
  grey500: 0xc9c9c9,
  grey600: 0xa8a8a8,
  grey700: 0x8f8f8f,
  grey800: 0x7d7d7d,
  grey900: 0x747474,
  grey1000: 0x28282a,
  grey1100: 0x0d0d0d,

  // Teal scale
  teal100: 0xdefffb,
  teal200: 0xddfef6,
  teal300: 0xccf9f1,
  teal400: 0xb1f7ec,
  teal500: 0x82eddb,
  teal600: 0x00e3c4,
  teal700: 0x00a995,
  teal800: 0x00907e,
  teal900: 0x007d6f,
  teal1000: 0x003f34,

  // Blue scale
  blue100: 0xf1f5ff,
  blue200: 0xf0f4ff,
  blue300: 0xe5edff,
  blue400: 0xd6e2ff,
  blue500: 0xb2c8ff,
  blue600: 0x8aa7ff,
  blue700: 0x416ff9,
  blue800: 0x2153e8,
  blue900: 0x275af1,
  blue1000: 0x0b2166,

  // Purple scale
  purple100: 0xfaf0ff,
  purple200: 0xf9f0ff,
  purple300: 0xf6e8ff,
  purple400: 0xf2d9ff,
  purple500: 0xdfa7ff,
  purple600: 0xc979ff,
  purple700: 0xa000f8,
  purple800: 0x8500d1,
  purple900: 0x7d00cc,
  purple1000: 0x2f004e,

  // Pink scale
  pink100: 0xffe8f6,
  pink200: 0xffe8f3,
  pink300: 0xffdfeb,
  pink400: 0xffd3e1,
  pink500: 0xfdb3cc,
  pink600: 0xf97ea7,
  pink700: 0xf22782,
  pink800: 0xe4106e,
  pink900: 0xc41562,
  pink1000: 0x460523,

  // Red scale
  red100: 0xffeeef,
  red200: 0xffe8ea,
  red300: 0xffe3e4,
  red400: 0xffd6d7,
  red500: 0xffb1b3,
  red600: 0xef7171,
  red700: 0xe7323f,
  red800: 0xd6222b,
  red900: 0xc62128,
  red1000: 0x47000c,

  // Yellow scale
  yellow100: 0xfff6de,
  yellow200: 0xfff4cf,
  yellow300: 0xfff1c1,
  yellow400: 0xffdc73,
  yellow500: 0xffc543,
  yellow600: 0xffaf00,
  yellow700: 0xffaf00,
  yellow800: 0xff9400,
  yellow900: 0xaa4d00,
  yellow1000: 0x561900,

  // Green scale
  green100: 0xecfdec,
  green200: 0xe5fce7,
  green300: 0xd3fad1,
  green400: 0xb9f5bc,
  green500: 0x82eb8d,
  green600: 0x79de6e,
  green700: 0x28a948,
  green800: 0x279141,
  green900: 0x107d32,
  green1000: 0x003900,

  // Semantic defaults
  teal: 0x007d6f,
  blue: 0x275af1,
  purple: 0x7d00cc,
  pink: 0xc41562,
  red: 0xc62128,
  yellow: 0xaa4d00,
  green: 0x107d32,
  muted: 0x747474,
  positive: 0x107d32,
  negative: 0xc62128,
};

export const DARK: Theme = {
  // Backgrounds
  background: 0x000000,
  backgroundSecondary: 0x000000,
  backgroundRaised: 0x17181a,

  // Text
  foreground: 0xededed,
  textSecondary: 0xa8a8a8,
  textTertiary: 0x7a7a7a,
  textKnockout: 0xffffff,

  // Grey scale
  grey000: 0x0d0d0d,
  grey100: 0x1a1a1a,
  grey200: 0x1f1f1f,
  grey300: 0x292929,
  grey400: 0x2e2e2e,
  grey500: 0x454545,
  grey600: 0x878787,
  grey700: 0x8f8f8f,
  grey800: 0x7a7a7a,
  grey900: 0xa8a8a8,
  grey1000: 0xededed,
  grey1100: 0xffffff,

  // Teal scale
  teal100: 0x00231b,
  teal200: 0x002b22,
  teal300: 0x003d34,
  teal400: 0x004035,
  teal500: 0x006154,
  teal600: 0x009b86,
  teal700: 0x00a995,
  teal800: 0x00907e,
  teal900: 0x00cbb5,
  teal1000: 0xcbfff5,

  // Blue scale
  blue100: 0x081744,
  blue200: 0x081c59,
  blue300: 0x152b70,
  blue400: 0x16307e,
  blue500: 0x1f3c93,
  blue600: 0x4d8bff,
  blue700: 0x4a75f7,
  blue800: 0x2354e7,
  blue900: 0x80a0fe,
  blue1000: 0xf0f4ff,

  // Purple scale
  purple100: 0x290c33,
  purple200: 0x341142,
  purple300: 0x47185e,
  purple400: 0x541a76,
  purple500: 0x642290,
  purple600: 0x9440d5,
  purple700: 0x9440d5,
  purple800: 0x7d2bba,
  purple900: 0xc472fb,
  purple1000: 0xfbecff,

  // Pink scale
  pink100: 0x310d1e,
  pink200: 0x420c25,
  pink300: 0x571032,
  pink400: 0x5d0c34,
  pink500: 0x76063f,
  pink600: 0xba0056,
  pink700: 0xf12b82,
  pink800: 0xe7006d,
  pink900: 0xff4d8d,
  pink1000: 0xffe9f4,

  // Red scale
  red100: 0x330a11,
  red200: 0x440d13,
  red300: 0x5d0e17,
  red400: 0x6f101b,
  red500: 0x88151f,
  red600: 0xf32e40,
  red700: 0xf13242,
  red800: 0xe2162a,
  red900: 0xff565f,
  red1000: 0xffe9ed,

  // Yellow scale
  yellow100: 0x2a1700,
  yellow200: 0x361900,
  yellow300: 0x502800,
  yellow400: 0x5b3000,
  yellow500: 0x703e00,
  yellow600: 0xed9a00,
  yellow700: 0xffaf00,
  yellow800: 0xff9400,
  yellow900: 0xff9400,
  yellow1000: 0xfff3d5,

  // Green scale
  green100: 0x002608,
  green200: 0x00320b,
  green300: 0x003a0e,
  green400: 0x004615,
  green500: 0x006717,
  green600: 0x00952d,
  green700: 0x00ac3a,
  green800: 0x009432,
  green900: 0x00ca50,
  green1000: 0xd8ffe4,

  // Semantic defaults
  teal: 0x00cbb5,
  blue: 0x80a0fe,
  purple: 0xc472fb,
  pink: 0xff4d8d,
  red: 0xff565f,
  yellow: 0xff9400,
  green: 0x00ca50,
  muted: 0xa8a8a8,
  positive: 0x00ca50,
  negative: 0xff565f,
};

export interface CanvasConfig {
  canvasId: string;
  createPlugin: () => GAME.Plugin | GAME.Plugin[];
  setupUI?: (state: GAME.State, theme: Theme) => void;
  beforeInit?: (worldElement: HTMLElement) => void;
  theme?: Theme;
}

export interface CanvasInstance {
  canvas: HTMLCanvasElement;
  worldElement: HTMLElement;
  config: CanvasConfig;
  state?: GAME.State;
  isVisible: boolean;
}

declare global {
  interface HTMLCanvasElement {
    __state__?: GAME.State;
    __stop__?: () => void;
  }
}
